import React, { useState } from 'react';
import { Button, Input, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { ProductSelectionPurchasePriceScope } from '../../modules/products/productSelectionApi';
import { useProductSelectionCatalog } from '../../modules/products/useProductSelectionCatalog';
import type { ProductVariant } from '../../types/pz';
import ProductPickerModal from './ProductPickerModal';
import './ProductNameCombobox.css';

interface ProductNameComboboxProps {
  value?: string;
  onChange: (productName: string) => void;
  onSelectVariant: (variantId: string | undefined, variant?: ProductVariant) => void;
  placeholder?: string;
  disabled?: boolean;
  'data-testid'?: string;
  showInventory?: boolean;
  preferredVariantIds?: readonly string[];
  selectedVariantId?: string | null;
  purchasePriceScope?: ProductSelectionPurchasePriceScope;
}

const ProductNameCombobox: React.FC<ProductNameComboboxProps> = ({
  value,
  onChange,
  onSelectVariant,
  placeholder,
  disabled = false,
  'data-testid': dataTestId,
  showInventory = true,
  preferredVariantIds = [],
  selectedVariantId,
  purchasePriceScope,
}) => {
  const { t } = useTranslation(['products']);
  const { variants, loading, reload } = useProductSelectionCatalog(true, purchasePriceScope);
  const [pickerOpen, setPickerOpen] = useState(false);

  return (
    <>
      <Space.Compact className="product-name-combobox">
        <Input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          placeholder={placeholder}
          data-testid={dataTestId}
        />
        <Button
          icon={<SearchOutlined />}
          disabled={disabled}
          onClick={() => setPickerOpen(true)}
          aria-label={t('products:catalogPicker.openButton')}
        >
          {t('products:catalogPicker.chooseButton')}
        </Button>
      </Space.Compact>

      <ProductPickerModal
        open={pickerOpen}
        variants={variants}
        loading={loading}
        selectedVariantId={selectedVariantId}
        preferredVariantIds={preferredVariantIds}
        showInventory={showInventory}
        onRefresh={reload}
        onSelect={(variant) => {
          onSelectVariant(variant.id, variant);
          setPickerOpen(false);
        }}
        onCancel={() => setPickerOpen(false)}
      />
    </>
  );
};

export default ProductNameCombobox;
